import { useEffect, useRef } from "react";
import { useQueries } from "@tanstack/react-query";

import { useGlobalStore } from "@/context/useGlobalStore";
import PinSizeService from "@/services/PinSizeService";
import LevelService from "@/services/LevelService";
import DefaultSettingService from "@/services/DefaultSettingService";
import WalkerService from "@/services/WalkerService";
import GroupMemberService from "@/services/GroupMemberService";
import CommitmentService from "@/services/CommitmentService";
import WalkDistanceService from "@/services/WalkDistanceService";
import AuthService from "@/services/AuthService";

const QUERY_CONFIG = { limit: 50, page: 1 };
const STALE_5MIN = 5 * 60 * 1000;
const GC_30MIN = 30 * 60 * 1000;

const Q_5MIN = {
    staleTime: STALE_5MIN,
    gcTime: GC_30MIN,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0,
    notifyOnChangeProps: ["data", "status"],
};

const Q_INF = {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0,
    notifyOnChangeProps: ["data"],
};

const getOptionalByIp = async (fetcher) => {
    try {
        return await fetcher();
    } catch (error) {
        const status = Number(error?.response?.status || error?.status || 0);
        if (status === 404) return { data: null };
        throw error;
    }
};

export function useLayoutBootstrap() {
    const setStates = useGlobalStore((s) => s.setStates);
    // Read once at mount — no subscription, no re-render when ipAddress is written back to the store.
    const storedIpAddress = useRef(useGlobalStore.getState()?.ipAddress).current;
    const coreSignatureRef = useRef("");
    const ipSignatureRef = useRef("");

    // --- Critical queries (start immediately) ---
    const [
        { data: pinSizes = [], status: pinsStatus },
        { data: levels = [], status: levelsStatus },
        { data: settings, status: settingsStatus },
        { data: ipAddress },
    ] = useQueries({
        queries: [
            {
                ...Q_5MIN,
                queryKey: ["pinSizes"],
                queryFn: async () => (await PinSizeService.getAll(QUERY_CONFIG))?.data?.data || [],
            },
            {
                ...Q_5MIN,
                queryKey: ["levels", "all"],
                queryFn: async () => (await LevelService.getMapAll(QUERY_CONFIG))?.data?.data || [],
            },
            {
                ...Q_5MIN,
                queryKey: ["default-settings"],
                queryFn: async () => (await DefaultSettingService.getAll())?.data || null,
            },
            {
                ...Q_5MIN,
                queryKey: ["ip-address"],
                // queryFn: () => isLocalhost() ? "::1" : getIP(),
                queryFn: async () => (await AuthService.getIP())?.ip ?? null,
                enabled: !storedIpAddress,
                initialData: storedIpAddress || undefined,
                staleTime: Infinity,
                gcTime: Infinity,
            },
        ],
    });

    const coreLoaded =
        pinsStatus === "success" &&
        levelsStatus === "success" &&
        settingsStatus === "success";

    // --- Deferred queries (start only after core is ready, so they don't compete during initial load) ---
    const [
        { data: commitments = [], status: commitmentStatus },
        { data: distances = [], status: distanceStatus },
    ] = useQueries({
        queries: [
            {
                ...Q_5MIN,
                queryKey: ["commitment-all"],
                queryFn: async () => (await CommitmentService.getAll())?.data?.data || [],
                enabled: coreLoaded,
            },
            {
                ...Q_5MIN,
                queryKey: ["walk-distance-all"],
                queryFn: async () => (await WalkDistanceService.getAll())?.data?.data || [],
                enabled: coreLoaded,
            },
        ],
    });

    // --- IP-dependent queries ---
    const [
        { data: walkerIPDetails, refetch: refetchWalkerIP },
        { data: groupMemberIPDetails, refetch: refetchGroupMemberIP },
    ] = useQueries({
        queries: [
            {
                ...Q_INF,
                queryKey: ["walker-ip", ipAddress],
                queryFn: () => getOptionalByIp(() => WalkerService.getByIP(ipAddress)),
                enabled: !!ipAddress,
            },
            {
                ...Q_INF,
                queryKey: ["group-member-ip", ipAddress],
                queryFn: () => getOptionalByIp(() => GroupMemberService.getByIP(ipAddress)),
                enabled: !!ipAddress,
            },
        ],
    });

    // Effect 1: pins/levels/settings — syncs as soon as these 3 are ready, no IP wait.
    useEffect(() => {
        if (!coreLoaded || !settings) return;

        const defaultPinSize = settings?.pin_size?.value || "medium";
        const sortedLevels = [...levels].sort((a, b) => a?.id - b?.id);
        const resolvedDefaultPin = pinSizes?.find((item) => item?.slug === defaultPinSize) || null;

        const signature = JSON.stringify({
            pinSizes: pinSizes.map((item) => `${item?.id ?? ""}:${item?.slug ?? ""}`).join("|"),
            levels: sortedLevels.map((item) => item?.id ?? "").join("|"),
            defaultPinSlug: resolvedDefaultPin?.slug || null,
            settingsId: settings?.id ?? null,
            settingsUpdatedAt: settings?.updated_at ?? null,
            settingsVersion: settings?.version ?? null,
        });

        if (coreSignatureRef.current === signature) return;
        coreSignatureRef.current = signature;

        setStates({
            defaultSettings: settings,
            pinSizes,
            defaultPinSize: resolvedDefaultPin,
            levels: sortedLevels,
        });
    }, [coreLoaded, pinSizes, levels, settings, setStates]);

    // Effect 2: IP + walker data — syncs independently, never blocks Effect 1.
    useEffect(() => {
        if (!ipAddress) return;
        const walkerData = walkerIPDetails?.data || null;
        const groupMemberData = groupMemberIPDetails?.data || null;

        const signature = JSON.stringify({
            ipAddress: ipAddress || null,
            walkerId: walkerData?.id ?? walkerData?.walker_id ?? null,
            walkerUpdatedAt: walkerData?.updated_at ?? null,
            walkerJoinedCount: walkerData?.joined_walks?.length ?? 0,
            groupMemberId: groupMemberData?.id ?? groupMemberData?.group_member_id ?? null,
            groupMemberUpdatedAt: groupMemberData?.updated_at ?? null,
            groupJoinedCount: groupMemberData?.joined_group_walks?.length ?? 0,
        });

        if (ipSignatureRef.current === signature) return;
        ipSignatureRef.current = signature;

        setStates({
            ipAddress,
            walkerIPDetails: walkerData,
            groupMemberIPDetails: groupMemberData,
            refetchWalkerIP,
            refetchGroupMemberIP,
        });
    }, [ipAddress, walkerIPDetails, groupMemberIPDetails, refetchWalkerIP, refetchGroupMemberIP, setStates]);

    // Effect 3: commitments/distances — deferred, never blocks HTML.
    useEffect(() => {
        if (commitmentStatus !== "success" || distanceStatus !== "success") return;
        setStates({ commitments, distances });
    }, [commitmentStatus, distanceStatus, commitments, distances, setStates]);
}
